import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Page from "../Page";
import DataTable, { DataColumn } from "../DataTable";
import { SearchInput } from "../Filters";
import { GET_IDFA_REPORT_ORDERS } from "../../api/graphql/idfaReports";
import { IdfaImportResult } from "../../api/idfaReports";
import ImportResults from "./ImportResults";
import { ExportButtons } from "./OrderIdfaReport";
import useIdfaUpload from "./useIdfaUpload";
import styles from "./styles.module.scss";

type ReportOrder = {
  orderType: string;
  orderId: number;
  hashId: number | null;
  appTitle: string | null;
  storeAppId: string | null;
  country: string | null;
  userEmail: string | null;
  timezone: string;
  files: number;
  rows: number;
  firstCompletedAt: string | null;
  lastCompletedAt: string | null;
  lastImportedAt: string | null;
};

const dayOf = (value: string | null) => (value ? value.slice(0, 10) : "");

const columns: DataColumn<ReportOrder>[] = [
  {
    key: "hashId",
    title: "Order",
    width: 96,
    fixed: true,
    render: (order) => (
      <Link
        to={`/campaigns/${order.orderType}/${order.orderId}`}
        className={styles.link}
      >
        #{order.hashId ?? order.orderId}
      </Link>
    ),
    sorter: (a, b) => (a.hashId || 0) - (b.hashId || 0),
  },
  {
    key: "app",
    title: "App",
    width: 140,
    mobile: "full",
    render: (order) => (
      <span title={order.storeAppId ? `ID ${order.storeAppId}` : undefined}>
        {order.appTitle || order.storeAppId || "—"}
      </span>
    ),
    sorter: (a, b) => (a.appTitle || "").localeCompare(b.appTitle || ""),
  },
  {
    key: "country",
    title: "Country",
    width: 110,
    render: (order) => order.country || "—",
    sorter: (a, b) => (a.country || "").localeCompare(b.country || ""),
  },
  {
    key: "userEmail",
    title: "Client",
    width: 176,
    mobile: "full",
    render: (order) => order.userEmail || "—",
    sorter: (a, b) => (a.userEmail || "").localeCompare(b.userEmail || ""),
  },
  {
    key: "rows",
    title: "IDFAs",
    width: 96,
    render: (order) => order.rows.toLocaleString("en-US"),
    sorter: (a, b) => a.rows - b.rows,
  },
  {
    key: "period",
    title: "Period",
    width: 210,
    // Days in the client's timezone, as the export writes them.
    render: (order) =>
      order.firstCompletedAt ? (
        <span title={`Days in the client's timezone, ${order.timezone}`}>
          {dayOf(order.firstCompletedAt)} – {dayOf(order.lastCompletedAt)}
        </span>
      ) : (
        "—"
      ),
    sorter: (a, b) =>
      (a.firstCompletedAt || "").localeCompare(b.firstCompletedAt || ""),
  },
  {
    key: "lastImportedAt",
    title: "Imported",
    width: 150,
    render: (order) =>
      order.lastImportedAt
        ? dayjs(order.lastImportedAt).format("DD.MM.YYYY HH:mm")
        : "—",
    sorter: (a, b) =>
      (a.lastImportedAt || "").localeCompare(b.lastImportedAt || ""),
  },
  {
    key: "export",
    title: "Export",
    width: 136,
    mobile: "footer",
    render: (order) => (
      <span className={styles.inlineActions}>
        <ExportButtons
          orderType={order.orderType}
          orderId={order.orderId}
          size="small"
        />
      </span>
    ),
  },
];

/**
 * The contractor's IDFA archives: upload one as it came, see where each file
 * went, and export any order's IDFAs.
 */
const IdfaReports: React.FC = () => {
  const { data, loading, refetch } = useQuery(GET_IDFA_REPORT_ORDERS, {
    fetchPolicy: "cache-and-network",
  });
  const [lastImport, setLastImport] = useState<IdfaImportResult | null>(null);
  const [search, setSearch] = useState("");
  const upload = useIdfaUpload((result) => {
    setLastImport(result);
    refetch();
  });

  const orders: ReportOrder[] = useMemo(
    () => data?.getIdfaReportOrders ?? [],
    [data],
  );
  const visibleOrders = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return orders;
    return orders.filter((order) =>
      [order.hashId, order.appTitle, order.storeAppId, order.userEmail]
        .map((value) => String(value ?? "").toLowerCase())
        .some((value) => value.includes(query)),
    );
  }, [orders, search]);

  return (
    <Page
      title="IDFA reports"
      subtitle="Upload the contractor's archive as it came: each file goes to the order its archive names (编号). Times are converted from China time to each client's timezone."
      actions={
        <>
          {upload.input}
          <Button
            type="primary"
            icon={<UploadOutlined />}
            loading={upload.uploading}
            onClick={upload.open}
          >
            Upload archive
          </Button>
        </>
      }
    >
      {lastImport && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Last upload</h2>
          <ImportResults result={lastImport} id="idfa-import-results" />
        </section>
      )}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Orders with IDFAs</h2>
          <div className={styles.actions}>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Order ID, app or email"
            />
          </div>
        </div>
        {loading && !data ? (
          <p className={styles.meta}>Loading…</p>
        ) : (
          <div className={styles.tableBox}>
            <DataTable
              id="idfa-report-orders"
              columns={columns}
              rows={visibleOrders}
              rowKey={(order) => `${order.orderType}:${order.orderId}`}
              emptyText={
                orders.length
                  ? "Nothing matches the search"
                  : "No IDFAs yet: upload the contractor's archive"
              }
              resetKey={search}
            />
          </div>
        )}
      </section>
    </Page>
  );
};

export default IdfaReports;
